import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { FileText, Plus, Paperclip, ArrowRight, CheckCircle } from "@phosphor-icons/react";

/** Shown right after a job request is converted: draft a fresh RAMS for the new job, copy an existing RAMS onto it
 *  (site details, operatives and nearest A&E are refreshed), or just open the job. */
export function PostConvertRamsDialog({ result, onClose }) {
  const navigate = useNavigate();
  const [existing, setExisting] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (!result) return;
    setPick("");
    API.get("/hs/rams").then((r) => setExisting((r.data.rams || []).filter((x) => x.status !== "archived" && x.job_id !== result.job_id))).catch(() => setExisting([]));
  }, [result]);
  if (!result) return null;
  const goJob = () => { onClose(); navigate(`/jobs/${result.job_id}`); };
  const create = async () => {
    setBusy("create");
    try {
      const { data } = await API.post("/hs/rams", { job_id: result.job_id });
      toast.success(`${data.number} drafted for ${result.job_ref}`); onClose(); navigate(`/health-safety/rams/${data.id}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't create the RAMS"); } finally { setBusy(""); }
  };
  const attach = async () => {
    setBusy("attach");
    try {
      const { data } = await API.post(`/hs/rams/${pick}/duplicate`, { job_id: result.job_id });
      toast.success(`${data.number} attached to ${result.job_ref} — site details and nearest A&E refreshed`); onClose(); navigate(`/health-safety/rams/${data.id}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't attach the RAMS"); } finally { setBusy(""); }
  };
  const attached = result.rams_attached || [];
  return (
    <Dialog open={!!result} onOpenChange={(o) => !o && !busy && goJob()}>
      <DialogContent className="max-w-lg" data-testid="post-convert-rams-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo] flex items-center gap-2"><CheckCircle size={18} className="text-emerald-600" /> Job {result.job_ref} created</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Does this job need a Risk Assessment &amp; Method Statement before the engineer arrives?</p>
        {attached.length > 0 && <p className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200" data-testid="post-convert-rams-attached">{attached.join(", ")} drafted on the request {attached.length === 1 ? "has" : "have"} been attached to the job.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          <button type="button" onClick={create} disabled={!!busy} className="rounded-lg border border-border p-3 text-left hover:border-primary/60 hover:bg-primary/5 transition-colors" data-testid="post-convert-create-rams">
            <p className="font-medium text-sm flex items-center gap-2"><Plus size={16} className="text-primary" /> {busy === "create" ? "Drafting…" : "Create RAMS"}</p>
            <p className="text-xs text-muted-foreground mt-1">New RAMS pre-filled from the job — client, site, scope, operatives and the nearest A&amp;E. You can add an AI first draft next.</p>
          </button>
          <div className="rounded-lg border border-border p-3 space-y-2" data-testid="post-convert-attach-rams">
            <p className="font-medium text-sm flex items-center gap-2"><Paperclip size={16} className="text-primary" /> Attach an existing RAMS</p>
            <Select value={pick || "__none__"} onValueChange={(v) => setPick(v === "__none__" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="post-convert-attach-select"><SelectValue placeholder="Choose a RAMS to copy" /></SelectTrigger>
              <SelectContent className="max-h-64"><SelectItem value="__none__">Choose a RAMS to copy…</SelectItem>{existing.map((r) => <SelectItem key={r.id} value={r.id}>{r.number} · {r.title}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" className="w-full h-8" disabled={!pick || !!busy} onClick={attach} data-testid="post-convert-attach-go"><FileText size={14} className="mr-1" /> {busy === "attach" ? "Copying…" : "Copy onto this job"}</Button>
            <p className="text-[11px] text-muted-foreground">Hazards, method and PPE are kept; site details, operatives and A&amp;E are refreshed. Engineers sign the copy.</p>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={goJob} disabled={!!busy} data-testid="post-convert-skip">Not now — open the job <ArrowRight size={14} className="ml-1" /></Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
